/**
 * A segmented control: a labelled group of native buttons, one pressed
 * (aria-pressed). `value` may be null when none applies (e.g. a custom date
 * window), so nothing claims to be selected that is not.
 */
import React, { useId } from 'react';
import { Marker } from './Marker';
import type { MarkerShape } from './tokens';

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: string;
  marker?: { shape: MarkerShape; color: string };
}

export interface SegmentedProps<V extends string = string> {
  label: string;
  options: Array<SegmentedOption<V>>;
  value: V | null;
  onChange(value: V): void;
  /** Keep the label for assistive technology only. */
  hideLabel?: boolean;
  className?: string;
}

export default function Segmented<V extends string = string>({
  label, options, value, onChange, hideLabel = false, className = '',
}: SegmentedProps<V>) {
  const labelId = useId();
  return (
    <div role="group" aria-labelledby={labelId} className={`tide-seg ${className}`.trim()}>
      <span id={labelId} className={hideLabel ? 'tide-sr' : 'tide-seg__label'}>{label}</span>
      <span className="tide-seg__track">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className="tide-seg__btn"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
          >
            {o.marker && <Marker shape={o.marker.shape} color={o.marker.color} size={10} />}
            {o.label}
          </button>
        ))}
      </span>
    </div>
  );
}
